import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Dropdown, Offcanvas } from "react-bootstrap";
import { IconAlertTriangle, IconBook, IconChevronLeft, IconChevronRight, IconDownload, IconFileDescription, IconRefresh } from "@tabler/icons-react";
import LayoutDashboard from "layouts/layout-dashboard-collapse";
import { deleteRequest, getRequest, patchRequest, postRequest } from "services/api";
import { buildQueryString } from "utils/queryHelpers";
import { useSearchParams } from "react-router-dom";
import * as XLSX from "xlsx";
import Swal from "sweetalert2";
import BookkeepingTransferModal, { useAccountingV2Transactions } from "./bookkeeping-transfer-modal";
import { buildTrfIndex, fetchTrfEntries, findTrfMatches, getDateBounds } from "./trf-duplicate-check";
import { buildBulkFailureHtml, getBulkFailures, runBulkRequest, runChunkedBulkRequest } from "./bulk-api";
import { requestOnce } from "services/api";
import "./bookkeeping-page.css";

const TABS = [
  { id: "kas-atv", label: "Kas ATV", description: "Penerimaan dan pengeluaran kas ATV" },
  { id: "kas-fb", label: "Kas FB", description: "Penerimaan kas food & beverage" },
  { id: "nam", label: "NAM", description: "ATV Voucher (NAM) dari Accounting V2" },
  { id: "others", label: "Others", description: "Others dari Accounting V2" },
  { id: "josh", label: "Josh", description: "Fee Josh (PJ + BM/MM)" },
  { id: "trf-bca", label: "Trf BCA", description: "Mutasi rekening BCA" },
  { id: "trf-mandiri", label: "Trf Mandiri", description: "Mutasi rekening Mandiri" },
  { id: "trf-bri", label: "Trf BRI", description: "Mutasi rekening BRI" },
];

const SAMPLE_ROWS = {
  "kas-atv": [
    ["03-Jun-26", "KAS ATV", 132204000, 0],
    ["03-Jun-26", "KBA OBAT BAOAK, SAYUR, COD PAKET", 0, 551000],
    ["04-Jun-26", "KAS ATV", 108143000, 0],
    ["04-Jun-26", "KKJ UNTUK JRO DALAM OLEH PAK PUK", 0, 1000000],
    ["05-Jun-26", "KAS ATV", 127098500, 0],
    ["05-Jun-26", "KBA PEMBAYARAN PAC PAK ABI", 0, 500000],
  ],
  "kas-fb": [["26-May-26", "UANG KAS", 7301000, 0], ["27-May-26", "UANG KAS", 2644500, 0], ["28-May-26", "UANG KAS", 7708000, 0]],
  nam: [["26-May-26", "NAM", 174855000, 0], ["27-May-26", "NAM", 150200000, 0]],
  others: [["26-May-26", "OTHERS", 20900000, 0], ["27-May-26", "OTHERS", 23950000, 0], ["28-May-26", "OTHERS", 24050000, 0]],
  josh: [["26-May-26", "FEE JOSH", 7520000, 0], ["27-May-26", "FEE JOSH", 8820000, 0], ["28-May-26", "FEE JOSH", 8855000, 0]],
  "trf-bca": [["", "ATV 3 TANDEM/OT/ADITYA ABHAY/VILLA KITO", 0, 2250000], ["", "ATV 4 SINGLE/OT/AMAN LUTHRA/VILLA LUNA", 0, 2000000]],
  "trf-mandiri": [["02-Jun-26", "ATV 1 TANDEM/OT/CHUN CHING FAN", 7500000, 0], ["04-Jun-26", "TRANSFER AMBIL TUNAI", 1000000, 0]],
  "trf-bri": [["03-Jun-26", "FOTO AMELINDA", 400000, 0]],
};

const formatNumber = (value) => Number(value || 0).toLocaleString("id-ID");

const parseNumberInput = (value) => Number(String(value || "").replace(/[^0-9-]/g, "")) || 0;

const formatDate = (value) => {
  if (!value) return "";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleDateString("id-ID", {
    day: "2-digit",
    month: "short",
    year: "2-digit",
  });
};

const toDateInputValue = (date) => {
  const value = date instanceof Date ? date : new Date(date);
  if (Number.isNaN(value.getTime())) return "";
  return [
    value.getFullYear(),
    String(value.getMonth() + 1).padStart(2, "0"),
    String(value.getDate()).padStart(2, "0"),
  ].join("-");
};

const getDateInputValue = (value) => {
  if (!value) return toDateInputValue(new Date());
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return String(value).slice(0, 10);
  return toDateInputValue(date);
};

const createDateAtNoon = (year, month, day) => new Date(year, month, day, 12, 0, 0, 0);

const getPeriodForDate = (date = new Date()) => {
  const current = new Date(date);
  const year = current.getFullYear();
  const month = current.getMonth();
  const start = current.getDate() >= 26
    ? createDateAtNoon(year, month, 26)
    : createDateAtNoon(year, month - 1, 26);
  const end = createDateAtNoon(start.getFullYear(), start.getMonth() + 1, 25);

  return {
    start: toDateInputValue(start),
    end: toDateInputValue(end),
  };
};

const getDefaultDateRange = () => getPeriodForDate();

// Tab & periode terakhir disimpan di URL (?tab=&start=&end=) dan localStorage,
// supaya saat kembali ke halaman ini tidak reset ke periode hari ini.
const BOOKKEEPING_LAST_VIEW_KEY = "bookkeeping:last-view";
const isDateParam = (value) => /^\d{4}-\d{2}-\d{2}$/.test(value || "");

const readStoredView = () => {
  try {
    return JSON.parse(window.localStorage.getItem(BOOKKEEPING_LAST_VIEW_KEY) || "null") || {};
  } catch (error) {
    return {};
  }
};

const getInitialView = (searchParams) => {
  const stored = readStoredView();
  const rawTab = searchParams.get("tab") || stored.tab;
  const tab = LEGACY_TAB_IDS[rawTab] || rawTab;
  const start = searchParams.get("start") || stored.start;
  const end = searchParams.get("end") || stored.end;
  return {
    tab: TABS.some((item) => item.id === tab) ? tab : TABS[0].id,
    range: isDateParam(start) && isDateParam(end) ? { start, end } : getDefaultDateRange(),
  };
};

const shiftDateRangeByMonth = (range, direction) => {
  const start = new Date(`${range.start}T12:00:00`);
  const end = new Date(`${range.end}T12:00:00`);
  if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime())) return getDefaultDateRange();

  start.setMonth(start.getMonth() + direction);
  end.setMonth(end.getMonth() + direction);
  return {
    start: toDateInputValue(start),
    end: toDateInputValue(end),
  };
};

const getResponseData = (response) => {
  const data = response?.data?.data || response?.data || {};
  return data.tabs || data;
};

const tabResponseKeys = {
  "kas-atv": "kas_atv",
  "kas-fb": "kas_fb",
  others: "others",
  josh: "josh",
  "trf-bca": "trf_bca",
  "trf-mandiri": "trf_mandiri_bri",
  "trf-bri": "trf_bri",
};

// Section API per tab. Trf Mandiri tetap memakai section lama `trf_mandiri_bri`
// supaya data yang sudah ada otomatis masuk ke Trf Mandiri; Trf BRI memakai section baru `trf_bri`.
// NAM & Others: dikirim manual per tanggal dari Accounting V2 (section `nam` & `others`, perlu ditambah di BE).
const additionalTransactionTabs = {
  nam: "nam",
  others: "others",
  josh: "josh",
  "trf-bca": "trf_bca",
  "trf-mandiri": "trf_mandiri_bri",
  "trf-bri": "trf_bri",
};

// Tab transfer: CRUD lewat popup (klik baris) + pilih transaksi dari Accounting V2.
const transferTabs = new Set(["trf-bca", "trf-mandiri", "trf-bri"]);

// Tujuan pindah / salin antar tab transfer.
const TRANSFER_TARGETS = [
  { tabId: "trf-bca", section: "trf_bca", label: "Trf BCA", shortLabel: "BCA" },
  { tabId: "trf-mandiri", section: "trf_mandiri_bri", label: "Trf Mandiri", shortLabel: "Mandiri" },
  { tabId: "trf-bri", section: "trf_bri", label: "Trf BRI", shortLabel: "BRI" },
];
const getTransferTargets = (tabId) => TRANSFER_TARGETS.filter((target) => target.tabId !== tabId);

// Tab lama (sebelum dipisah) diarahkan ke Trf Mandiri.
const LEGACY_TAB_IDS = { "trf-mandiri-bri": "trf-mandiri" };

// ===== Urutan tab Trf =====
// Urutan di dalam satu tanggal ditentukan BE lewat sort_order (data baru / hasil copy & pindah
// masuk di bawah data yang sudah ada, sesuai urutan yang dikirim). BE mengirim tanggal DESC;
// FE hanya membalik urutan TANGGAL jadi ascending dengan sort stabil, tanpa mengubah urutan di dalam tanggal.
const sortTransferRows = (rows) => [...rows].sort((a, b) => {
  const dateA = a.rawDate || "";
  const dateB = b.rawDate || "";
  if (dateA === dateB) return 0;
  return dateA < dateB ? -1 : 1;
});

const buildTransferPayload = (row, section) => {
  const description = String(row.description || "").trim();
  return {
    date: getDateInputValue(row.rawDate),
    section,
    // Trf BCA / Mandiri / BRI selalu masuk Debet.
    entry_type: "debit",
    name: description || "-",
    amount: Number(row.debit || row.credit || 0),
    description: description || null,
    notes: row.notes || null,
    reservation_id: row.reservationId || null,
    reservation_income_id: row.reservationIncomeId || null,
  };
};

const additionalTabDefaultEntryTypes = {
  nam: "debit",
  others: "debit",
  josh: "debit",
  "trf-bca": "debit",
  "trf-mandiri": "debit",
  "trf-bri": "debit",
};

const getApiId = (row) => {
  const apiId = row.apiId || row.id;
  return String(apiId || "").startsWith("temp-") ? null : apiId;
};

const createBlankAdditionalRow = (tabId) => {
  const entryType = additionalTabDefaultEntryTypes[tabId] || "credit";
  return {
    id: `temp-${Date.now()}`,
    rawDate: toDateInputValue(new Date()),
    date: formatDate(new Date()),
    description: "",
    debit: entryType === "debit" ? 0 : 0,
    credit: entryType === "credit" ? 0 : 0,
    entryType,
    isNew: true,
  };
};

const getTabRows = (tabData) => {
  if (Array.isArray(tabData)) return tabData;
  if (!tabData || typeof tabData !== "object") return [];
  if (Array.isArray(tabData.data)) return tabData.data;
  if (Array.isArray(tabData.rows)) return tabData.rows;
  if (Array.isArray(tabData.details)) return tabData.details;
  if (Array.isArray(tabData.summary?.rows)) return tabData.summary.rows;
  return [];
};

const normalizeRows = (rows, isDummy = false, tabId = "") => getTabRows(rows).map((row, index) => {
  if (Array.isArray(row)) {
    const [date, description, debit, credit] = row;
    return {
      id: `${tabId || "row"}-${index}`,
      date,
      rawDate: date,
      description,
      debit: Number(debit) || 0,
      credit: Number(credit) || 0,
      isDummy,
    };
  }

  const taxAmount = Number(row.tax ?? row.total_tax ?? 0) || 0;
  const amount = Number(row.amount ?? row.total_amount ?? row.saldo ?? row.grand_total ?? row.total ?? 0) || 0;
  const description =
    row.description ||
    row.notes ||
    row.name ||
    row.keterangan ||
    row.code ||
    row.payment_method_name ||
    row.guest_name ||
    "";

  if (tabId === "kas-atv") {
    const rowAmount = Number(row.amount ?? 0) || 0;
    return {
      id: row.id || row.apiId || `${tabId}-${index}`,
      apiId: getApiId(row),
      date: formatDate(row.date || row.transaction_date || row.inserted_at || row.created_at),
      rawDate: getDateInputValue(row.date || row.transaction_date || row.inserted_at || row.created_at),
      description,
      debit: Number(row.debit ?? 0) || (row.entry_type === "debit" ? rowAmount : 0),
      credit: Number(row.credit ?? 0) || (row.entry_type === "credit" ? rowAmount : 0),
      isDummy: Boolean(row.isDummy || isDummy),
    };
  }

  // Format lama (Others dihitung BE dari pajak); sekarang tab Others memakai additional transactions.
  if (tabId === "others" && !additionalTransactionTabs[tabId]) {
    return {
      id: row.id || row.apiId || `${tabId}-${index}`,
      apiId: getApiId(row),
      date: formatDate(row.date || row.transaction_date || row.inserted_at || row.created_at),
      rawDate: getDateInputValue(row.date || row.transaction_date || row.inserted_at || row.created_at),
      description: description ? `OTHERS - ${description}` : "OTHERS",
      debit: taxAmount,
      credit: 0,
      isDummy: Boolean(row.isDummy || isDummy),
    };
  }

  if (additionalTransactionTabs[tabId]) {
    const rowAmount = Number(row.amount ?? 0) || 0;
    const entryType = row.entry_type || additionalTabDefaultEntryTypes[tabId];
    let debit = Number(row.debit ?? 0) || (entryType === "debit" ? rowAmount : 0);
    let credit = Number(row.credit ?? 0) || (entryType === "credit" ? rowAmount : 0);
    // Tab transfer hanya punya kolom Debet; data lama yang tersimpan sebagai kredit ikut ditampilkan di Debet.
    if (transferTabs.has(tabId)) {
      debit += credit;
      credit = 0;
    }
    const rowDate = row.rawDate || row.date || row.transaction_date || row.inserted_at || row.created_at;

    return {
      id: row.id || row.apiId || `${tabId}-${index}`,
      apiId: getApiId(row),
      date: formatDate(rowDate),
      rawDate: getDateInputValue(rowDate),
      description,
      debit,
      credit,
      entryType,
      reservationId: row.reservation_id || null,
      reservationIncomeId: row.reservation_income_id || null,
      createdAt: row.created_at || null,
      notes: row.notes || null,
      isDirty: Boolean(row.__dirty),
      isDummy: Boolean(row.isDummy || isDummy),
    };
  }

  if (transferTabs.has(tabId)) {
    return {
      id: row.id || row.apiId || `${tabId}-${index}`,
      apiId: getApiId(row),
      date: formatDate(row.date || row.transaction_date || row.inserted_at || row.created_at),
      rawDate: getDateInputValue(row.date || row.transaction_date || row.inserted_at || row.created_at),
      description,
      debit: 0,
      credit: amount,
      isDummy: Boolean(row.isDummy || isDummy),
    };
  }

  return {
    id: row.id || row.apiId || `${tabId}-${index}`,
    apiId: getApiId(row),
    date: formatDate(row.date || row.transaction_date || row.inserted_at || row.created_at),
    rawDate: getDateInputValue(row.date || row.transaction_date || row.inserted_at || row.created_at),
    description,
    debit: Number(row.debit ?? row.debet ?? row.total_debit ?? 0) || 0,
    credit: Number(row.credit ?? row.kredit ?? row.total_credit ?? 0) || 0,
    isDummy: Boolean(row.isDummy || isDummy),
  };
});

const getTabSummaryRows = (tabData, tabId) => {
  if (!tabData || typeof tabData !== "object") return [];

  if (tabId === "kas-atv") {
    const paymentCharge = Number(tabData.total_payment_charge ?? tabData.summary?.total_payment_charge ?? 0)
      || (tabData.payment_breakdown || tabData.summary?.payment_breakdown || []).reduce(
        (total, item) => total + (Number(item.charge_amount) || 0),
        0
      );
    const rows = [
      ["", "KAS ATV - Net Revenue", Number(tabData.net_revenue ?? tabData.summary?.net_revenue ?? 0), 0],
      ["", "Payment Charge", 0, paymentCharge],
      ["", "Biaya", 0, Number(tabData.total_biaya ?? tabData.summary?.total_biaya ?? 0)],
      ["", "Biaya Transport", 0, Number(tabData.total_biaya_transport ?? tabData.summary?.total_biaya_transport ?? 0)],
      ["", "Cash On Hand Deduction", 0, Number(tabData.total_cash_on_hand_deduction ?? tabData.summary?.total_cash_on_hand_deduction ?? 0)],
    ].filter(([, , debit, credit]) => debit || credit);
    return normalizeRows(rows);
  }

  if (tabId === "kas-fb" && (tabData.total_debit || tabData.total_credit)) {
    return normalizeRows([["", "KAS FB", tabData.total_debit || 0, tabData.total_credit || 0]]);
  }

  if (transferTabs.has(tabId) && tabData.total_amount) {
    return normalizeRows([["", tabData.payment_method_name_matched || "Transfer", 0, tabData.total_amount]]);
  }

  return [];
};

const getErrorMessage = (error) => {
  const message = error?.response?.data?.message || error?.data?.message || error?.message || "";
  if (!message) return "";
  if (message.includes("Unknown column")) return "Kolom yang diminta belum tersedia di API.";
  if (message.includes("Requested filter")) return "Filter endpoint belum sesuai kebutuhan FE.";
  return message.length > 140 ? `${message.slice(0, 140)}...` : message;
};

const BookkeepingTable = ({
  rows,
  editable = false,
  savingRows = {},
  onAddRow,
  onDeleteRow,
  onRowChange,
  onSaveRow,
  onRowClick,
  onAddClick,
  addLabel = "+ Tambah transaksi",
  rowActions = [],
  actionHeader = "AKSI",
  selection,
  hideCredit = false,
  reorder,
}) => {
  const [dragSelectMode, setDragSelectMode] = useState(null);
  // Urutan manual: seret ikon ⠿ di kolom NO ke posisi lain DI TANGGAL YANG SAMA.
  const [dragRow, setDragRow] = useState(null);
  const [dropTarget, setDropTarget] = useState(null);
  const canReorderRow = (row) => Boolean(reorder && !reorder.disabled && !row.isDummy && row.apiId);
  const isValidDropTarget = (row) => Boolean(dragRow && row.id !== dragRow.id && row.rawDate === dragRow.rawDate && canReorderRow(row));
  const clearDrag = () => {
    setDragRow(null);
    setDropTarget(null);
  };
  // Sel angka yang sedang diedit: tampilkan angka mentah (tanpa titik) supaya kursor tidak loncat.
  const [focusedAmountCell, setFocusedAmountCell] = useState(null);
  const getAmountInputProps = (row, field) => {
    const cellKey = `${row.id}-${field}`;
    const value = Number(row[field]) || 0;
    return {
      value: focusedAmountCell === cellKey ? (value ? String(value) : "") : (value ? formatNumber(value) : ""),
      placeholder: "0",
      inputMode: "numeric",
      onFocus: (event) => {
        setFocusedAmountCell(cellKey);
        const input = event.currentTarget;
        // Blok isi sel supaya ketikan pertama langsung mengganti nilai (seperti Excel / Accounting V2).
        window.requestAnimationFrame(() => input.select());
      },
      onChange: (event) => onRowChange(row.id, field, parseNumberInput(event.target.value)),
      onBlur: () => {
        setFocusedAmountCell(null);
        onSaveRow(row);
      },
    };
  };
  const totals = rows.reduce((result, row) => ({ debit: result.debit + row.debit, credit: result.credit + row.credit }), { debit: 0, credit: 0 });
  const displayRows = rows.map((row, index) => ({
    ...row,
    displayDate: index === 0 || row.date !== rows[index - 1]?.date ? row.date : "",
  }));
  const hasActionColumn = editable || rowActions.length > 0;
  // NO + TGL + KETERANGAN sebelum kolom angka.
  const labelColSpan = 3;
  const amountColSpan = hideCredit ? 1 : 2;
  const isSelectableRow = (row) => Boolean(selection && !row.isDummy && row.apiId);

  // Pilih baris dengan klik / seret di kolom tanggal (mode pilih atau batal pilih
  // ditentukan dari baris pertama yang diklik).
  useEffect(() => {
    if (!dragSelectMode) return undefined;
    const stopDrag = () => setDragSelectMode(null);
    window.addEventListener("mouseup", stopDrag);
    return () => window.removeEventListener("mouseup", stopDrag);
  }, [dragSelectMode]);

  const handleSelectStart = (event, row) => {
    if (!isSelectableRow(row) || selection.disabled) return;
    event.preventDefault();
    event.stopPropagation();
    const mode = selection.selected[row.id] ? "deselect" : "select";
    setDragSelectMode(mode);
    selection.onApply(row, mode);
  };

  const handleSelectEnter = (row) => {
    if (!dragSelectMode || !isSelectableRow(row)) return;
    selection.onApply(row, dragSelectMode);
  };

  // ===== Navigasi keyboard seperti Excel =====
  // Enter / Shift+Enter: turun / naik, Tab / Shift+Tab: kanan / kiri, panah: pindah sel
  // (kiri/kanan pindah sel kalau kursor di ujung teks atau isi sel terblok), Esc: batalkan perubahan sel.
  const gridColumns = ["date", "description", "debit", ...(hideCredit ? [] : ["credit"])];
  const gridFields = { date: "rawDate", description: "description", debit: "debit", credit: "credit" };
  const focusOriginalRef = useRef(null);

  const focusGridCell = (tableElement, rowIndex, column) => {
    const target = tableElement?.querySelector(`[data-grid-row="${rowIndex}"][data-grid-col="${column}"]`);
    if (!target) return false;
    target.focus();
    target.scrollIntoView({ block: "nearest", inline: "nearest" });
    window.requestAnimationFrame(() => {
      try {
        target.select();
      } catch (error) {
        // input type="date" tidak mendukung select().
      }
    });
    return true;
  };

  const getCaretInfo = (input) => {
    try {
      const { selectionStart, selectionEnd, value } = input;
      if (selectionStart === null || selectionStart === undefined) return null;
      return {
        atStart: selectionStart === 0 && selectionEnd === 0,
        atEnd: selectionStart === value.length && selectionEnd === value.length,
        allSelected: selectionStart === 0 && selectionEnd === value.length,
      };
    } catch (error) {
      return null;
    }
  };

  const handleGridFocus = (event) => {
    const { gridRow, gridCol } = event.target.dataset || {};
    if (gridRow === undefined || !gridCol) return;
    const row = displayRows[Number(gridRow)];
    focusOriginalRef.current = row ? { rowId: row.id, field: gridFields[gridCol], value: row[gridFields[gridCol]] } : null;
  };

  const handleCellKeyDown = (event) => {
    const input = event.currentTarget;
    const rowIndex = Number(input.dataset.gridRow);
    const column = input.dataset.gridCol;
    const columnIndex = gridColumns.indexOf(column);
    if (Number.isNaN(rowIndex) || columnIndex < 0) return;
    const tableElement = input.closest(".bookkeeping-table");
    const lastColumnIndex = gridColumns.length - 1;

    const moveTo = (nextRow, nextColumnIndex) => {
      event.preventDefault();
      focusGridCell(tableElement, nextRow, gridColumns[nextColumnIndex]);
    };

    switch (event.key) {
      case "Enter":
        moveTo(rowIndex + (event.shiftKey ? -1 : 1), columnIndex);
        break;
      case "ArrowDown":
        moveTo(rowIndex + 1, columnIndex);
        break;
      case "ArrowUp":
        moveTo(rowIndex - 1, columnIndex);
        break;
      case "Tab":
        if (event.shiftKey) {
          if (columnIndex > 0) moveTo(rowIndex, columnIndex - 1);
          else if (rowIndex > 0) moveTo(rowIndex - 1, lastColumnIndex);
        } else if (columnIndex < lastColumnIndex) moveTo(rowIndex, columnIndex + 1);
        else moveTo(rowIndex + 1, 0);
        break;
      case "ArrowLeft":
      case "ArrowRight": {
        const caret = getCaretInfo(input);
        const goLeft = event.key === "ArrowLeft";
        // Tanggal & sel yang terblok penuh langsung pindah sel; teks biasa pindah kalau kursor di ujung.
        const canLeave = !caret || caret.allSelected || (goLeft ? caret.atStart : caret.atEnd);
        if (!canLeave) return;
        const nextColumnIndex = columnIndex + (goLeft ? -1 : 1);
        if (nextColumnIndex >= 0 && nextColumnIndex <= lastColumnIndex) moveTo(rowIndex, nextColumnIndex);
        break;
      }
      case "Escape": {
        const original = focusOriginalRef.current;
        const row = displayRows[rowIndex];
        if (!original || !row || original.rowId !== row.id) return;
        event.preventDefault();
        onRowChange(row.id, original.field, original.value);
        window.requestAnimationFrame(() => {
          try {
            input.select();
          } catch (error) {
            // abaikan untuk input tanggal
          }
        });
        break;
      }
      default:
        break;
    }
  };

  return (
    <div className="bookkeeping-table-wrap">
      {onAddClick && (
        <div className="bookkeeping-inline-actions">
          <button type="button" onClick={onAddClick}>{addLabel}</button>
          <span>
            {editable ? "Edit langsung seperti Excel: Enter / Tab / panah untuk pindah sel, Esc untuk batal. Tersimpan otomatis." : "Klik baris untuk edit."}
            {selection ? " Klik / seret kolom NO untuk memilih beberapa transaksi." : ""}
            {reorder ? " Seret ikon ⠿ untuk mengubah urutan dalam tanggal yang sama." : ""}
          </span>
        </div>
      )}
      {editable && onAddRow && (
        <div className="bookkeeping-inline-actions">
          <button type="button" onClick={onAddRow}>+ Tambah baris</button>
          <span>Isi langsung di tabel, lalu klik area luar kolom untuk menyimpan.</span>
        </div>
      )}
      <table className="bookkeeping-table" onFocus={editable ? handleGridFocus : undefined}>
        <thead>
          <tr>
            <th className="bookkeeping-no-cell">NO</th>
            <th>TGL</th>
            <th>KETERANGAN</th>
            <th className="bookkeeping-amount-col">DEBET</th>
            {!hideCredit && <th className="bookkeeping-amount-col">KREDIT</th>}
            {hasActionColumn && <th className="bookkeeping-action-col">{actionHeader}</th>}
          </tr>
        </thead>
        <tbody>
          {displayRows.map((row, index) => {
            const isSelected = Boolean(selection?.selected[row.id]);
            const selectable = isSelectableRow(row);
            return (
            <tr
              key={row.id || `${row.date}-${index}`}
              className={[
                row.isDummy ? "bookkeeping-dummy-row" : "",
                onRowClick && !row.isDummy ? "bookkeeping-clickable-row" : "",
                isSelected ? "bookkeeping-selected-row" : "",
              ].filter(Boolean).join(" ")}
              onClick={onRowClick && !row.isDummy ? () => onRowClick(row) : undefined}
              title={onRowClick && !row.isDummy ? "Klik untuk edit" : undefined}
              onDragOver={dragRow ? (event) => {
                if (!isValidDropTarget(row)) return;
                event.preventDefault();
                event.dataTransfer.dropEffect = "move";
                const rect = event.currentTarget.getBoundingClientRect();
                const position = event.clientY < rect.top + rect.height / 2 ? "before" : "after";
                if (dropTarget?.id !== row.id || dropTarget?.position !== position) setDropTarget({ id: row.id, position });
              } : undefined}
              onDrop={dragRow ? (event) => {
                event.preventDefault();
                if (isValidDropTarget(row) && dropTarget?.id === row.id) reorder.onReorder(dragRow, row, dropTarget.position);
                clearDrag();
              } : undefined}
              data-drop-position={dropTarget?.id === row.id ? dropTarget.position : undefined}
              data-dragging={dragRow?.id === row.id ? "true" : undefined}
            >
              <td
                className={["bookkeeping-no-cell", selectable ? "bookkeeping-selectable-no" : ""].filter(Boolean).join(" ")}
                onMouseDown={selectable ? (event) => handleSelectStart(event, row) : undefined}
                onMouseEnter={selectable ? () => handleSelectEnter(row) : undefined}
                onClick={selectable ? (event) => event.stopPropagation() : undefined}
                title={selectable ? "Klik / seret untuk memilih" : undefined}
              >
                {canReorderRow(row) && (
                  <span
                    className="bookkeeping-drag-handle"
                    draggable
                    title="Seret untuk mengubah urutan (dalam tanggal yang sama)"
                    aria-label="Seret untuk mengubah urutan"
                    onMouseDown={(event) => event.stopPropagation()}
                    onClick={(event) => event.stopPropagation()}
                    onDragStart={(event) => {
                      event.stopPropagation();
                      event.dataTransfer.effectAllowed = "move";
                      event.dataTransfer.setData("text/plain", String(row.id));
                      const rowElement = event.currentTarget.closest("tr");
                      if (rowElement) event.dataTransfer.setDragImage(rowElement, 16, 12);
                      setDragRow(row);
                    }}
                    onDragEnd={clearDrag}
                  >
                    ⠿
                  </span>
                )}
                {savingRows[row.id] ? <span className="bookkeeping-row-saving" aria-label="Menyimpan" /> : isSelected ? "✓" : index + 1}
              </td>
              <td className={`${row.displayDate ? "bookkeeping-date-cell" : "bookkeeping-empty-date-cell"}${editable ? " bookkeeping-input-cell" : ""}`}>
                {editable ? (
                  <input
                    className={`bookkeeping-cell-input text-center ${row.displayDate ? "" : "bookkeeping-repeated-date-input"}`}
                    type="date"
                    value={row.rawDate || ""}
                    onChange={(event) => onRowChange(row.id, "rawDate", event.target.value)}
                    onKeyDown={handleCellKeyDown}
                    onBlur={() => onSaveRow(row)}
                    title={row.rawDate || ""}
                    data-bookkeeping-cell
                    data-grid-row={index}
                    data-grid-col="date"
                  />
                ) : row.displayDate}
              </td>
              <td className={`bookkeeping-desc-cell${editable ? " bookkeeping-input-cell" : ""}`}>
                {editable ? (
                  <input
                    className="bookkeeping-cell-input"
                    value={row.description || ""}
                    onChange={(event) => onRowChange(row.id, "description", event.target.value)}
                    onKeyDown={handleCellKeyDown}
                    onBlur={() => onSaveRow(row)}
                    placeholder="Keterangan..."
                    data-bookkeeping-cell
                    data-grid-row={index}
                    data-grid-col="description"
                  />
                ) : <span>{row.description}</span>}
                {row.isDummy && <span className="bookkeeping-dummy-badge">DUMMY</span>}
              </td>
              <td className={`${row.debit < 0 ? "negative" : "number"}${editable ? " bookkeeping-input-cell" : ""}`}>
                {editable ? (
                  <input
                    className="bookkeeping-cell-input text-end"
                    {...getAmountInputProps(row, "debit")}
                    onKeyDown={handleCellKeyDown}
                    title="Isi kolom ini jika transaksi masuk sebagai Debet"
                    data-bookkeeping-cell
                    data-grid-row={index}
                    data-grid-col="debit"
                  />
                ) : formatNumber(row.debit)}
              </td>
              {!hideCredit && (
                <td className={`${row.credit < 0 ? "negative" : "number"}${editable ? " bookkeeping-input-cell" : ""}`}>
                  {editable ? (
                    <input
                      className="bookkeeping-cell-input text-end"
                      {...getAmountInputProps(row, "credit")}
                      onKeyDown={handleCellKeyDown}
                      title="Isi kolom ini jika transaksi masuk sebagai Kredit"
                      data-bookkeeping-cell
                      data-grid-row={index}
                      data-grid-col="credit"
                    />
                  ) : formatNumber(row.credit)}
                </td>
              )}
              {hasActionColumn && (
                <td className="text-center">
                  <div className="bookkeeping-row-actions">
                    {!row.isDummy && row.apiId && rowActions.map((action) => (action.targets ? (
                      <Dropdown key={action.key} onClick={(event) => event.stopPropagation()}>
                        <Dropdown.Toggle
                          as="button"
                          type="button"
                          className="bookkeeping-move-btn"
                          disabled={savingRows[row.id]}
                          title={action.title}
                        >
                          {action.label}
                        </Dropdown.Toggle>
                        <Dropdown.Menu popperConfig={{ strategy: "fixed" }} className="bookkeeping-action-menu">
                          <Dropdown.Header>{action.key === "copy" ? "Salin ke" : "Pindah ke"}</Dropdown.Header>
                          {action.targets.map((target) => (
                            <Dropdown.Item key={target.tabId} onClick={() => action.onClick(row, target)}>
                              {target.label}
                            </Dropdown.Item>
                          ))}
                        </Dropdown.Menu>
                      </Dropdown>
                    ) : (
                      <button
                        key={action.key}
                        type="button"
                        className="bookkeeping-move-btn"
                        onClick={(event) => {
                          event.stopPropagation();
                          action.onClick(row);
                        }}
                        disabled={savingRows[row.id]}
                        title={action.title}
                      >
                        {action.label}
                      </button>
                    )))}
                    {editable && (
                      <button
                        type="button"
                        className="bookkeeping-delete-btn"
                        onClick={() => onDeleteRow(row)}
                        disabled={savingRows[row.id]}
                      >
                        Hapus
                      </button>
                    )}
                  </div>
                </td>
              )}
            </tr>
            );
          })}
          <tr className="bookkeeping-total">
            <td colSpan={labelColSpan}>TOTAL</td>
            <td className="number">{formatNumber(totals.debit)}</td>
            {!hideCredit && <td className="number">{formatNumber(totals.credit)}</td>}
            {hasActionColumn && <td />}
          </tr>
          <tr className="bookkeeping-balance">
            <td colSpan={labelColSpan}>SALDO</td>
            <td colSpan={amountColSpan} className="number">{formatNumber(totals.debit - totals.credit)}</td>
            {hasActionColumn && <td />}
          </tr>
        </tbody>
      </table>
    </div>
  );
};

// Export tab aktif ke Excel, sama seperti yang tampil di tabel (urutan, total, saldo).
const exportBookkeepingToExcel = ({ tab, dateRange, rows, hideCredit }) => {
  const header = hideCredit ? ["NO", "TGL", "KETERANGAN", "DEBET"] : ["NO", "TGL", "KETERANGAN", "DEBET", "KREDIT"];
  const amountCols = hideCredit ? [3] : [3, 4];
  const sheetRows = [
    [`PEMBUKUAN - ${tab.label.toUpperCase()}`],
    [`Periode: ${formatDate(dateRange.start)} - ${formatDate(dateRange.end)}`],
    [],
    header,
  ];
  const numberRowIndexes = [];
  let totalDebit = 0;
  let totalCredit = 0;

  rows.forEach((row, index) => {
    totalDebit += Number(row.debit) || 0;
    totalCredit += Number(row.credit) || 0;
    numberRowIndexes.push(sheetRows.length);
    const line = [index + 1, row.date || "", row.description || "", Number(row.debit) || 0];
    if (!hideCredit) line.push(Number(row.credit) || 0);
    sheetRows.push(line);
  });

  numberRowIndexes.push(sheetRows.length);
  sheetRows.push(hideCredit ? ["", "", "TOTAL", totalDebit] : ["", "", "TOTAL", totalDebit, totalCredit]);
  numberRowIndexes.push(sheetRows.length);
  sheetRows.push(["", "", "SALDO", totalDebit - totalCredit]);

  const worksheet = XLSX.utils.aoa_to_sheet(sheetRows);
  numberRowIndexes.forEach((r) => {
    amountCols.forEach((c) => {
      const cell = worksheet[XLSX.utils.encode_cell({ r, c })];
      if (cell && cell.t === "n") cell.z = "#,##0";
    });
  });
  worksheet["!cols"] = [{ wch: 5 }, { wch: 12 }, { wch: 80 }, { wch: 15 }, ...(hideCredit ? [] : [{ wch: 15 }])];

  const workbook = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(workbook, worksheet, tab.label.replace(/[\\/?*[\]:]/g, "").slice(0, 31));

  // Blob + anchor supaya tetap jalan di WebView Android (sama seperti export lain di app ini).
  const excelBuffer = XLSX.write(workbook, { bookType: "xlsx", type: "array" });
  const blob = new Blob([excelBuffer], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" });
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.setAttribute("download", `Pembukuan_${tab.label.replace(/[^a-z0-9]+/gi, "_")}_${dateRange.start}_${dateRange.end}.xlsx`);
  document.body.appendChild(link);
  link.click();
  setTimeout(() => {
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
  }, 100);
};

const BookkeepingLoading = ({ label, detail }) => (
  <div className="bookkeeping-loading">
    <div className="bookkeeping-loading-spinner" />
    <div>
      <strong>Loading data pembukuan...</strong>
      <span>{detail || `Menunggu response API untuk ${label}.`}</span>
    </div>
  </div>
);

const BookkeepingPage = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [initialView] = useState(() => getInitialView(searchParams));
  const [activeTab, setActiveTab] = useState(initialView.tab);
  const [dateRange, setDateRange] = useState(initialView.range);

  useEffect(() => {
    const view = { tab: activeTab, start: dateRange.start, end: dateRange.end };
    setSearchParams(view, { replace: true });
    try {
      window.localStorage.setItem(BOOKKEEPING_LAST_VIEW_KEY, JSON.stringify(view));
    } catch (error) {
      // localStorage tidak tersedia (mis. private mode); URL param tetap jalan.
    }
  }, [activeTab, dateRange.start, dateRange.end, setSearchParams]);
  const [refreshing, setRefreshing] = useState(false);
  const [showDocCanvas, setShowDocCanvas] = useState(false);
  const [apiData, setApiData] = useState(null);
  const [apiError, setApiError] = useState("");
  const [savingRows, setSavingRows] = useState({});
  const active = TABS.find((tab) => tab.id === activeTab);
  const isUsingDummyData = !apiData;
  const isAdditionalTab = Boolean(additionalTransactionTabs[activeTab]);
  const isTransferTab = transferTabs.has(activeTab);
  const [transferModal, setTransferModal] = useState({ show: false, row: null });
  const [transferSaving, setTransferSaving] = useState(false);
  const [transferDeleting, setTransferDeleting] = useState(false);
  // Daftar Accounting V2 hanya diambil saat popup "+ Tambah transaksi" dibuka (bukan setiap buka tab),
  // dan disimpan per periode supaya buka-tutup popup tidak memanggil server lagi.
  const {
    transactions: accountingV2Transactions,
    loading: accountingV2Loading,
    error: accountingV2Error,
  } = useAccountingV2Transactions(isTransferTab && transferModal.show, dateRange);
  // Tab Trf menunggu data Accounting V2 juga, supaya tabel langsung tampil dengan urutan final (tidak loncat).
  const isLoadingData = refreshing && !apiError;
  const [selectedTransfers, setSelectedTransfers] = useState({});
  const [bulkProgress, setBulkProgress] = useState(null);
  const selectedTransferRows = Object.values(selectedTransfers);
  const rows = useMemo(() => {
    if (!apiData) return normalizeRows(SAMPLE_ROWS[activeTab], true);
    const tabData = activeTab === "kas-atv" || additionalTransactionTabs[activeTab]
      ? apiData
      : apiData[tabResponseKeys[activeTab]];
    const normalized = normalizeRows(tabData, false, activeTab);
    if (transferTabs.has(activeTab) && normalized.length) return sortTransferRows(normalized);
    return normalized.length ? normalized : getTabSummaryRows(tabData, activeTab);
  }, [activeTab, apiData]);

  // Total baris terpilih diambil dari data terbaru (bukan snapshot saat dipilih), supaya edit debet langsung ikut terhitung.
  const selectedTransferTotals = useMemo(() => {
    const selectedIds = new Set(Object.keys(selectedTransfers));
    return rows.reduce((acc, row) => (selectedIds.has(String(row.id))
      ? { debit: acc.debit + (Number(row.debit) || 0), credit: acc.credit + (Number(row.credit) || 0) }
      : acc), { debit: 0, credit: 0 });
  }, [rows, selectedTransfers]);

  const loadBookkeeping = useCallback(async () => {
    setRefreshing(true);
    setApiError("");
    setApiData(null);
    try {
      const query = buildQueryString({ start_date: dateRange.start, end_date: dateRange.end });
      const endpoint = activeTab === "kas-atv"
        ? `/accounting/bookkeeping/kas-atv${query}`
        : additionalTransactionTabs[activeTab]
          ? `/accounting/additional-transactions${buildQueryString({
            start_date: dateRange.start,
            end_date: dateRange.end,
            section: additionalTransactionTabs[activeTab],
            all: 1,
          })}`
        : `/accounting/bookkeeping${query}`;
      const response = await getRequest(endpoint);
      setApiData(getResponseData(response));
    } catch (error) {
      const detail = getErrorMessage(error);
      setApiError(`Data API belum tersedia, sementara menampilkan data contoh.${detail ? ` Detail: ${detail}` : ""}`);
      setApiData(null);
    } finally {
      setRefreshing(false);
    }
  }, [activeTab, dateRange.end, dateRange.start]);

  useEffect(() => { loadBookkeeping(); }, [loadBookkeeping]);
  // Pilihan baris direset setiap ganti tab / periode.
  useEffect(() => { setSelectedTransfers({}); }, [activeTab, dateRange.start, dateRange.end]);

  const updateAdditionalRow = (rowId, field, value) => {
    if (!isAdditionalTab) return;
    setApiData((prevData) => getTabRows(prevData).map((row) => {
      const rowKey = row.id || row.apiId;
      if (rowKey !== rowId) return row;
      const nextRow = { ...row, [field]: value, __dirty: true };

      if (field === "rawDate") {
        nextRow.date = formatDate(value);
      }
      if (field === "debit" && value > 0) {
        nextRow.credit = 0;
        nextRow.entry_type = "debit";
      }
      if (field === "credit" && value > 0) {
        nextRow.debit = 0;
        nextRow.entry_type = "credit";
      }

      return nextRow;
    }));
  };

  const addAdditionalRow = () => {
    if (!isAdditionalTab) return;
    setApiData((prevData) => [...getTabRows(prevData), createBlankAdditionalRow(activeTab)]);
  };

  const saveAdditionalRow = async (row) => {
    if (!isAdditionalTab || row.isDummy) return;

    // Autosave hanya kalau ada perubahan, supaya pindah kolom tidak memanggil API terus.
    if (row.apiId && !row.isDirty) return;

    const amount = Number(row.debit || row.credit || 0);
    const name = String(row.description || "").trim();
    if (!name && amount === 0) return;

    const rowId = row.id || row.apiId;
    const entryType = transferTabs.has(activeTab)
      ? "debit"
      : row.debit > 0 ? "debit" : row.credit > 0 ? "credit" : additionalTabDefaultEntryTypes[activeTab];
    const payload = {
      date: getDateInputValue(row.rawDate),
      section: additionalTransactionTabs[activeTab],
      entry_type: entryType,
      name: name || "-",
      amount,
      description: row.description || null,
      notes: row.notes || null,
      ...(transferTabs.has(activeTab) && {
        reservation_id: row.reservationId || null,
        reservation_income_id: row.reservationIncomeId || null,
      }),
    };

    setSavingRows((prev) => ({ ...prev, [rowId]: true }));
    try {
      const response = row.apiId
        ? await patchRequest(`/accounting/additional-transactions/${row.apiId}`, payload)
        : await postRequest("/accounting/additional-transactions", payload);
      const saved = response?.data || response;

      setApiData((prevData) => getTabRows(prevData).map((item) => {
        const itemKey = item.id || item.apiId;
        return itemKey === rowId
          ? {
            ...saved,
            entry_type: saved.entry_type || entryType,
            debit: entryType === "debit" ? Number(saved.amount ?? amount) : 0,
            credit: entryType === "credit" ? Number(saved.amount ?? amount) : 0,
          }
          : item;
      }));
    } catch (error) {
      setApiError(error.message || "Gagal menyimpan transaksi tambahan.");
    } finally {
      setSavingRows((prev) => {
        const next = { ...prev };
        delete next[rowId];
        return next;
      });
    }
  };

  const deleteAdditionalRow = async (row) => {
    if (!isAdditionalTab || row.isDummy) return;
    const rowId = row.id || row.apiId;

    setSavingRows((prev) => ({ ...prev, [rowId]: true }));
    try {
      if (row.apiId) await deleteRequest(`/accounting/additional-transactions/${row.apiId}`);
      setApiData((prevData) => getTabRows(prevData).filter((item) => (item.id || item.apiId) !== rowId));
    } catch (error) {
      setApiError(error.message || "Gagal menghapus transaksi tambahan.");
    } finally {
      setSavingRows((prev) => {
        const next = { ...prev };
        delete next[rowId];
        return next;
      });
    }
  };

  const handleExportExcel = () => {
    if (isUsingDummyData || rows.length === 0) {
      Swal.fire("Pemberitahuan", "Tidak ada data untuk diexport.", "warning");
      return;
    }
    try {
      exportBookkeepingToExcel({ tab: active, dateRange, rows, hideCredit: isTransferTab });
    } catch (error) {
      console.error(error);
      Swal.fire("Gagal", "Export Excel gagal diproses.", "error");
    }
  };

  const openTransferModal = (row = null) => setTransferModal({ show: true, row });
  const closeTransferModal = () => setTransferModal({ show: false, row: null });

  const saveTransferEntry = async (form) => {
    const row = transferModal.row;
    const description = String(form.description || "").trim();
    const payload = {
      date: form.date,
      section: additionalTransactionTabs[activeTab],
      entry_type: "debit",
      name: description,
      amount: Number(form.amount) || 0,
      description,
      notes: row?.notes || null,
      reservation_id: form.reservationId || null,
      reservation_income_id: form.reservationIncomeId || null,
    };

    setTransferSaving(true);
    try {
      if (row?.apiId) await patchRequest(`/accounting/additional-transactions/${row.apiId}`, payload);
      else await postRequest("/accounting/additional-transactions", payload);
      closeTransferModal();
      await loadBookkeeping();
    } catch (error) {
      Swal.fire("Gagal", getErrorMessage(error) || "Gagal menyimpan transaksi transfer.", "error");
    } finally {
      setTransferSaving(false);
    }
  };

  // Konfirmasi salin / pindah sekaligus cek apakah transaksinya sudah ada di tab tujuan (cegah dobel).
  // Mengembalikan baris yang jadi diproses, atau null kalau dibatalkan.
  const confirmTransferWithDuplicateCheck = async ({ rows: candidates, target, verb }) => {
    const bounds = getDateBounds(candidates.map((row) => row.rawDate));
    let duplicates = [];
    let checkFailed = false;

    Swal.fire({
      title: "Mengecek data dobel...",
      text: `Memastikan transaksi belum ada di ${target.label}.`,
      allowOutsideClick: false,
      didOpen: () => Swal.showLoading(),
    });
    try {
      const index = buildTrfIndex(bounds ? await fetchTrfEntries({ sections: [target.section], ...bounds }) : []);
      duplicates = candidates.filter((row) => findTrfMatches(
        { reservationId: row.reservationId, text: row.description, date: row.rawDate },
        index
      ).length > 0);
    } catch (error) {
      checkFailed = true;
    }

    const count = candidates.length;
    const subject = count === 1 ? (candidates[0].description || "transaksi ini") : `${count} transaksi`;

    if (duplicates.length === 0) {
      const confirmation = await Swal.fire({
        title: `${verb} ke ${target.label}?`,
        text: checkFailed
          ? `Tidak bisa mengecek data dobel. Pastikan ${subject} belum ada di ${target.label}.`
          : subject,
        icon: checkFailed ? "warning" : "question",
        showCancelButton: true,
        confirmButtonText: `Ya, ${verb.toLowerCase()} ke ${target.shortLabel}`,
        cancelButtonText: "Batal",
      });
      return confirmation.isConfirmed ? candidates : null;
    }

    const duplicateIds = new Set(duplicates.map((row) => row.id));
    const nonDuplicates = candidates.filter((row) => !duplicateIds.has(row.id));
    const listHtml = duplicates
      .slice(0, 8)
      .map((row) => `• ${String(row.description || "-").replace(/</g, "&lt;")}`)
      .join("<br/>") + (duplicates.length > 8 ? `<br/>• ... dan ${duplicates.length - 8} lainnya` : "");

    const confirmation = await Swal.fire({
      icon: "warning",
      title: `${duplicates.length} transaksi sudah ada di ${target.label}`,
      html: `<div style="text-align:left;font-size:13px">${listHtml}</div>`,
      showConfirmButton: nonDuplicates.length > 0,
      confirmButtonText: `${verb} ${nonDuplicates.length} saja (lewati yang dobel)`,
      showDenyButton: true,
      denyButtonText: `Tetap ${verb.toLowerCase()} semua (${count})`,
      showCancelButton: true,
      cancelButtonText: "Batal",
    });
    if (confirmation.isConfirmed) return nonDuplicates;
    if (confirmation.isDenied) return candidates;
    return null;
  };

  const copyTransferEntry = async (row, target) => {
    if (!row?.apiId || !target) return;

    const confirmedRows = await confirmTransferWithDuplicateCheck({ rows: [row], target, verb: "Salin" });
    if (!confirmedRows?.length) return;

    const rowId = row.id || row.apiId;
    setSavingRows((prev) => ({ ...prev, [rowId]: true }));
    try {
      const response = await postRequest("/accounting/additional-transactions", buildTransferPayload(row, target.section));
      if (response && response.success === false) throw new Error(response.message);
      Swal.fire({
        icon: "success",
        title: `Disalin ke ${target.label}`,
        timer: 1400,
        showConfirmButton: false,
      });
    } catch (error) {
      Swal.fire("Gagal", getErrorMessage(error) || "Gagal menyalin transaksi.", "error");
    } finally {
      setSavingRows((prev) => {
        const next = { ...prev };
        delete next[rowId];
        return next;
      });
    }
  };

  const moveTransferEntry = async (row, target) => {
    if (!row?.apiId || !target) return;

    const confirmedRows = await confirmTransferWithDuplicateCheck({ rows: [row], target, verb: "Pindah" });
    if (!confirmedRows?.length) return;

    const rowId = row.id || row.apiId;
    setSavingRows((prev) => ({ ...prev, [rowId]: true }));
    setTransferSaving(true);
    try {
      await patchRequest(`/accounting/additional-transactions/${row.apiId}`, buildTransferPayload(row, target.section));
      closeTransferModal();
      // Baris hilang dari tab ini; langsung terlihat saat membuka tab tujuan.
      setApiData((prevData) => getTabRows(prevData).filter((item) => (item.id || item.apiId) !== rowId));
      Swal.fire({
        icon: "success",
        title: `Dipindah ke ${target.label}`,
        timer: 1400,
        showConfirmButton: false,
      });
    } catch (error) {
      Swal.fire("Gagal", getErrorMessage(error) || "Gagal memindahkan transaksi.", "error");
    } finally {
      setTransferSaving(false);
      setSavingRows((prev) => {
        const next = { ...prev };
        delete next[rowId];
        return next;
      });
    }
  };

  const applyTransferSelection = (row, mode) => {
    setSelectedTransfers((current) => {
      if (mode === "select" ? current[row.id] : !current[row.id]) return current;
      const next = { ...current };
      if (mode === "select") next[row.id] = row;
      else delete next[row.id];
      return next;
    });
  };

  const selectAllTransfers = () => {
    setSelectedTransfers(Object.fromEntries(rows.filter((row) => !row.isDummy && row.apiId).map((row) => [row.id, row])));
  };

  // Jalankan aksi massal satu per satu, tampilkan progres, lalu muat ulang data.
  // ===== Urutan manual (drag) di tab Trf =====
  // 2 logic urutan: (1) otomatis dari sort_order BE (data baru masuk di bawah), (2) manual: admin menyeret
  // baris ke posisi lain di tanggal yang sama, lalu urutan tanggal itu disimpan ke BE.
  const persistTransferOrder = async (section, date, orderedRows) => {
    const ids = orderedRows.map((row) => row.apiId);
    try {
      await requestOnce("/accounting/additional-transactions/reorder", "PUT", { section, date, ids });
      return;
    } catch (error) {
      // Endpoint reorder belum ada di BE (404/405/422) -> pakai bulk-move ke section yang sama:
      // BE menaruh ulang setiap baris di bawah sesuai urutan items, hasilnya = urutan baru.
      if (![404, 405, 422].includes(error?.status)) throw error;
    }
    const response = await runBulkRequest({
      url: "/accounting/additional-transactions/bulk-move",
      method: "PATCH",
      payload: { atomic: true, items: orderedRows.map((row) => ({ id: row.apiId, section })) },
    });
    if (response.rolledBack || !response.allOk) throw new Error(response.message || "Gagal menyimpan urutan.");
  };

  const reorderTransferRows = async (draggedRow, targetRow, position) => {
    const date = draggedRow.rawDate;
    const bucket = rows.filter((row) => row.rawDate === date && !row.isDummy && row.apiId);
    const withoutDragged = bucket.filter((row) => row.id !== draggedRow.id);
    let insertIndex = withoutDragged.findIndex((row) => row.id === targetRow.id);
    if (insertIndex < 0) return;
    if (position === "after") insertIndex += 1;
    const ordered = [...withoutDragged.slice(0, insertIndex), draggedRow, ...withoutDragged.slice(insertIndex)];
    if (ordered.every((row, index) => row.id === bucket[index]?.id)) return;

    // Optimistic: urutan langsung berubah di layar, lalu disimpan di belakang.
    const orderedIds = ordered.map((row) => row.id);
    setApiData((prevData) => {
      const list = [...getTabRows(prevData)];
      const byId = new Map(list.map((item) => [item.id || item.apiId, item]));
      const slots = list.reduce((acc, item, index) => (orderedIds.includes(item.id || item.apiId) ? [...acc, index] : acc), []);
      slots.forEach((slot, index) => {
        list[slot] = byId.get(orderedIds[index]);
      });
      return list;
    });

    const savingState = Object.fromEntries(orderedIds.map((id) => [id, true]));
    setSavingRows((prev) => ({ ...prev, ...savingState }));
    try {
      await persistTransferOrder(additionalTransactionTabs[activeTab], date, ordered);
    } catch (error) {
      Swal.fire("Gagal", getErrorMessage(error) || "Urutan gagal disimpan, dikembalikan ke urutan sebelumnya.", "error");
      await loadBookkeeping();
    } finally {
      setSavingRows((prev) => {
        const next = { ...prev };
        orderedIds.forEach((id) => delete next[id]);
        return next;
      });
    }
  };

  // Aksi massal = 1 request bulk (dipecah per 500), dikirim sesuai urutan tabel supaya
  // sort_order di tab tujuan mengikuti urutan yang terlihat.
  const runBulkTransferAction = async ({ label, successText, rowsToProcess, url, method = "POST", buildPayload }) => {
    const rowOrder = new Map(rows.map((row, index) => [row.id, index]));
    const targets = [...(rowsToProcess || selectedTransferRows)].sort((a, b) => (rowOrder.get(a.id) ?? 0) - (rowOrder.get(b.id) ?? 0));
    if (targets.length === 0) return;

    setBulkProgress({ label, total: targets.length });
    let response;
    try {
      response = await runChunkedBulkRequest({ url, method, items: targets, buildPayload });
    } catch (error) {
      setBulkProgress(null);
      Swal.fire("Gagal", getErrorMessage(error) || "Permintaan gagal diproses.", "error");
      return;
    }
    setBulkProgress(null);
    setSelectedTransfers({});
    await loadBookkeeping();

    const failures = getBulkFailures(response.results, targets, (row) => row?.description || row?.apiId || "-");
    if (failures.length === 0) {
      Swal.fire({ icon: "success", title: `${response.okCount} ${successText}`, timer: 1600, showConfirmButton: false });
      return;
    }
    Swal.fire({
      icon: response.okCount > 0 ? "warning" : "error",
      title: response.rolledBack
        ? "Dibatalkan: tidak ada data yang diubah"
        : `${response.okCount} berhasil, ${failures.length} gagal`,
      html: (response.rolledBack ? "<div class='mb-2 small'>Ada data yang gagal, jadi semua dibatalkan supaya tidak setengah jalan.</div>" : "")
        + buildBulkFailureHtml(failures.filter((failure) => !response.rolledBack || failure.result.status !== "skipped")),
    });
  };

  const bulkMoveTransfers = async (target) => {
    if (!target || selectedTransferRows.length === 0) return;
    const confirmedRows = await confirmTransferWithDuplicateCheck({ rows: selectedTransferRows, target, verb: "Pindah" });
    if (!confirmedRows?.length) return;

    await runBulkTransferAction({
      rowsToProcess: confirmedRows,
      label: `Memindahkan ke ${target.label}`,
      url: "/accounting/additional-transactions/bulk-move",
      method: "PATCH",
      buildPayload: (chunk) => ({ atomic: true, items: chunk.map((row) => ({ id: row.apiId, section: target.section })) }),
      successText: `transaksi dipindah ke ${target.label}`,
    });
  };

  const bulkCopyTransfers = async (target) => {
    if (!target || selectedTransferRows.length === 0) return;
    const confirmedRows = await confirmTransferWithDuplicateCheck({ rows: selectedTransferRows, target, verb: "Salin" });
    if (!confirmedRows?.length) return;

    await runBulkTransferAction({
      rowsToProcess: confirmedRows,
      label: `Menyalin ke ${target.label}`,
      url: "/accounting/additional-transactions/bulk",
      method: "POST",
      buildPayload: (chunk) => ({ atomic: false, items: chunk.map((row) => buildTransferPayload(row, target.section)) }),
      successText: `transaksi disalin ke ${target.label}`,
    });
  };

  const bulkDeleteTransfers = async () => {
    if (selectedTransferRows.length === 0) return;
    const confirmation = await Swal.fire({
      title: `Hapus ${selectedTransferRows.length} transaksi?`,
      text: "Data yang dihapus tidak bisa dikembalikan.",
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Ya, hapus",
      cancelButtonText: "Batal",
      confirmButtonColor: "#dc3545",
    });
    if (!confirmation.isConfirmed) return;

    await runBulkTransferAction({
      label: "Menghapus transaksi",
      url: "/accounting/additional-transactions/bulk-delete",
      method: "POST",
      buildPayload: (chunk) => ({ atomic: true, ids: chunk.map((row) => row.apiId) }),
      successText: "transaksi dihapus",
    });
  };

  const deleteTransferEntry = async (row) => {
    if (!row?.apiId) return;
    const confirmation = await Swal.fire({
      title: "Hapus transaksi?",
      text: row.description || "",
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Ya, hapus",
      cancelButtonText: "Batal",
      confirmButtonColor: "#dc3545",
    });
    if (!confirmation.isConfirmed) return;

    setTransferDeleting(true);
    try {
      await deleteRequest(`/accounting/additional-transactions/${row.apiId}`);
      closeTransferModal();
      await loadBookkeeping();
    } catch (error) {
      Swal.fire("Gagal", getErrorMessage(error) || "Gagal menghapus transaksi transfer.", "error");
    } finally {
      setTransferDeleting(false);
    }
  };

  return (
    <LayoutDashboard>
      <div className="bookkeeping-page container-fluid py-3">
        <div className="bookkeeping-heading">
          <div>
            <div className="text-muted small fw-bold text-uppercase">Accounting</div>
            <h1><IconBook size={28} /> Menu Pembukuan</h1>
            <p>Rekap kas, fee, dan transfer berdasarkan periode yang dipilih.</p>
          </div>
          <div className="bookkeeping-actions">
            <button className="btn btn-white border" onClick={() => setShowDocCanvas(true)}>
              <IconFileDescription size={16} className="me-1" /> Doc
            </button>
            <button
              className="btn btn-success"
              onClick={handleExportExcel}
              disabled={isLoadingData || isUsingDummyData || rows.length === 0}
              title={isUsingDummyData ? "Data contoh tidak bisa diexport" : `Export ${active.label} periode terpilih`}
            >
              <IconDownload size={16} className="me-1" /> Export Excel
            </button>
            <button className="btn btn-light border" onClick={loadBookkeeping} disabled={refreshing}>
              <IconRefresh size={16} className="me-1" /> {refreshing ? "Loading..." : "Refresh"}
            </button>
          </div>
        </div>
        <div className="card border-0 shadow-sm bookkeeping-filter">
          <div className="card-body">
            <div className="row g-3 align-items-end">
              <div className="col-sm-6 col-lg-3">
                <label>Mulai Tanggal</label>
                <input type="date" className="form-control" value={dateRange.start} onChange={(e) => setDateRange({ ...dateRange, start: e.target.value })} />
              </div>
              <div className="col-sm-6 col-lg-3">
                <label>Sampai Tanggal</label>
                <input type="date" className="form-control" value={dateRange.end} onChange={(e) => setDateRange({ ...dateRange, end: e.target.value })} />
              </div>
              <div className="col-lg-auto d-flex gap-2">
                <button type="button" className="btn btn-light border" onClick={() => setDateRange((current) => shiftDateRangeByMonth(current, -1))} title="Periode sebelumnya" aria-label="Periode sebelumnya">
                  <IconChevronLeft size={17} />
                </button>
                <button type="button" className="btn btn-light border" onClick={() => setDateRange((current) => shiftDateRangeByMonth(current, 1))} title="Periode berikutnya" aria-label="Periode berikutnya">
                  <IconChevronRight size={17} />
                </button>
              </div>
              <div className="col-lg">
                <div className="bookkeeping-filter-note">Default periode: tanggal 26 sampai tanggal 25. Tanggal tetap bisa diubah manual.</div>
              </div>
            </div>
          </div>
        </div>
        {apiError && <div className="alert alert-warning py-2 d-flex gap-2 align-items-start"><IconAlertTriangle size={18} className="mt-1" /><span>{apiError}</span></div>}
        <div className="bookkeeping-tabs">{TABS.map((tab) => <button key={tab.id} className={`bookkeeping-tab ${activeTab === tab.id ? "active" : ""}`} onClick={() => setActiveTab(tab.id)}><strong>{tab.label}</strong><span>{tab.description}</span></button>)}</div>
        <div className="card border-0 shadow-sm">
          <div className="card-header d-flex justify-content-between align-items-center">
            <div>
              <h2>{active.label}</h2>
              <span>{active.description}</span>
            </div>
            <div className="d-flex gap-2 align-items-center">
              <span className={`badge ${isLoadingData ? "bg-blue-lt text-blue" : isUsingDummyData ? "bg-yellow-lt text-yellow" : "bg-green-lt text-green"}`}>
                {isLoadingData ? "LOADING DATA" : isUsingDummyData ? "DUMMY DATA" : "DATA API"}
              </span>
              <span className="badge bg-blue-lt text-blue">{dateRange.start} s/d {dateRange.end}</span>
            </div>
          </div>
          {isTransferTab && !isLoadingData && (selectedTransferRows.length > 0 || bulkProgress) && (
            <div className="bookkeeping-bulk-bar" role="region" aria-label="Aksi transaksi terpilih">
              {bulkProgress ? (
                <>
                  <span className="bookkeeping-bulk-count">{bulkProgress.label} ({bulkProgress.total} transaksi)...</span>
                  <div className="bookkeeping-bulk-progress is-indeterminate">
                    <div />
                  </div>
                </>
              ) : (
                <>
                  <span className="bookkeeping-bulk-count">
                    {selectedTransferRows.length} transaksi dipilih
                    <span className="bookkeeping-bulk-sum">Debet: <b>{formatNumber(selectedTransferTotals.debit)}</b></span>
                    <span className="bookkeeping-bulk-sum">Kredit: <b>{formatNumber(selectedTransferTotals.credit)}</b></span>
                  </span>
                  <div className="d-flex gap-2 flex-wrap">
                    <Dropdown>
                      <Dropdown.Toggle size="sm" variant="primary">
                        Pindah ({selectedTransferRows.length}) ke
                      </Dropdown.Toggle>
                      <Dropdown.Menu popperConfig={{ strategy: "fixed" }}>
                        {getTransferTargets(activeTab).map((target) => (
                          <Dropdown.Item key={target.tabId} onClick={() => bulkMoveTransfers(target)}>{target.label}</Dropdown.Item>
                        ))}
                      </Dropdown.Menu>
                    </Dropdown>
                    <Dropdown>
                      <Dropdown.Toggle size="sm" variant="info" className="text-white">
                        Salin ({selectedTransferRows.length}) ke
                      </Dropdown.Toggle>
                      <Dropdown.Menu popperConfig={{ strategy: "fixed" }}>
                        {getTransferTargets(activeTab).map((target) => (
                          <Dropdown.Item key={target.tabId} onClick={() => bulkCopyTransfers(target)}>{target.label}</Dropdown.Item>
                        ))}
                      </Dropdown.Menu>
                    </Dropdown>
                    <button type="button" className="btn btn-sm btn-outline-danger" onClick={bulkDeleteTransfers}>
                      Hapus ({selectedTransferRows.length})
                    </button>
                    <button type="button" className="btn btn-sm btn-light border" onClick={selectAllTransfers}>
                      Pilih semua
                    </button>
                    <button type="button" className="btn btn-sm btn-light border" onClick={() => setSelectedTransfers({})}>
                      Batal pilih
                    </button>
                  </div>
                </>
              )}
            </div>
          )}
          {isLoadingData ? (
            <BookkeepingLoading
              label={active.label}
            />
          ) : (
            <BookkeepingTable
              rows={rows}
              editable={isAdditionalTab}
              savingRows={savingRows}
              onAddRow={isTransferTab ? undefined : addAdditionalRow}
              onDeleteRow={deleteAdditionalRow}
              onRowChange={updateAdditionalRow}
              onSaveRow={saveAdditionalRow}
              onAddClick={isTransferTab ? () => openTransferModal() : undefined}
              rowActions={isTransferTab ? [
                {
                  key: "move",
                  label: "→ Pindah",
                  title: "Pindahkan ke tab Trf lain (hilang dari tab ini)",
                  targets: getTransferTargets(activeTab),
                  onClick: moveTransferEntry,
                },
                {
                  key: "copy",
                  label: "⧉ Salin",
                  title: "Salin ke tab Trf lain (tetap ada di tab ini)",
                  targets: getTransferTargets(activeTab),
                  onClick: copyTransferEntry,
                },
              ] : []}
              selection={isTransferTab ? {
                selected: selectedTransfers,
                onApply: applyTransferSelection,
                disabled: Boolean(bulkProgress),
              } : undefined}
              hideCredit={isTransferTab}
              reorder={isTransferTab ? { onReorder: reorderTransferRows, disabled: Boolean(bulkProgress) } : undefined}
            />
          )}
        </div>
      </div>
      <BookkeepingTransferModal
        show={transferModal.show}
        row={transferModal.row}
        tabLabel={active.label}
        dateRange={dateRange}
        defaultEntryType={additionalTabDefaultEntryTypes[activeTab]}
        hideEntryType
        section={additionalTransactionTabs[activeTab]}
        transactions={accountingV2Transactions}
        transactionsLoading={accountingV2Loading}
        transactionsError={accountingV2Error}
        saving={transferSaving}
        deleting={transferDeleting}
        onHide={closeTransferModal}
        onSave={saveTransferEntry}
        onDelete={deleteTransferEntry}
      />
      <Offcanvas show={showDocCanvas} onHide={() => setShowDocCanvas(false)} placement="end" className="bookkeeping-doc-canvas">
        <Offcanvas.Header closeButton>
          <div>
            <div className="text-muted small fw-bold text-uppercase">Dokumentasi</div>
            <Offcanvas.Title>Menu Pembukuan</Offcanvas.Title>
          </div>
        </Offcanvas.Header>
        <Offcanvas.Body>
          <div className="bookkeeping-doc-section">
            <h3>Konsep Utama</h3>
            <p>
              Menu Pembukuan adalah rangkuman kas dan transfer untuk membantu admin mengecek
              arus uang masuk dan keluar dalam satu periode. Filter tanggal di atas berlaku
              untuk semua tab, jadi admin tidak perlu mengganti tanggal satu per satu.
            </p>
          </div>

          <div className="bookkeeping-doc-section">
            <h3>Cara Membaca Angka</h3>
            <ul>
              <li><strong>Debet</strong> adalah uang masuk atau penambahan kas.</li>
              <li><strong>Kredit</strong> adalah uang keluar atau pengurangan kas.</li>
              <li><strong>Saldo</strong> adalah hasil akhir dari total debet dikurangi total kredit.</li>
              <li>Label <strong>DUMMY DATA</strong> berarti data hanya contoh karena API belum mengembalikan data.</li>
            </ul>
          </div>

          <div className="bookkeeping-doc-section">
            <h3>Isi Setiap Tab</h3>
            <div className="bookkeeping-doc-grid">
              <div><strong>Kas ATV</strong><span>Penerimaan ATV dan pengeluaran yang berhubungan dengan operasional ATV.</span></div>
              <div><strong>Kas FB</strong><span>Rekap uang kas Food & Beverage dari laporan transaksi resto.</span></div>
              <div><strong>NAM</strong><span>ATV Voucher (NAM) per tanggal, dikirim manual dari Accounting V2 lewat tombol "Kirim ke Pembukuan".</span></div>
              <div><strong>Others</strong><span>Total OTHERS per tanggal, dikirim manual dari Accounting V2.</span></div>
              <div><strong>Josh</strong><span>Fee Josh (total PJ + BM/MM) per tanggal, dikirim manual dari Accounting V2.</span></div>
              <div><strong>Trf BCA</strong><span>Daftar transaksi transfer melalui rekening BCA. Klik baris untuk edit/hapus; transaksi bisa dipilih dari Accounting V2 dan tanggal otomatis mengikuti transaksinya.</span></div>
              <div><strong>Trf Mandiri</strong><span>Daftar transaksi transfer melalui rekening Mandiri (termasuk data lama dari tab Mandiri & BRI).</span></div>
              <div><strong>Trf BRI</strong><span>Daftar transaksi transfer melalui rekening BRI. Transaksi bisa dipindah / disalin antar tab Trf.</span></div>
            </div>
          </div>

          <div className="bookkeeping-doc-section">
            <h3>Sumber Data</h3>
            <p>
              Halaman ini membaca data dari endpoint Accounting Bookkeeping. Untuk Kas ATV,
              sumber finalnya mengikuti data Accounting V2 yang sudah dikerjakan admin,
              bukan data mentah booking saja.
            </p>
          </div>

          <div className="bookkeeping-doc-note">
            Jika angka terlihat kosong atau muncul label dummy, gunakan tombol Refresh terlebih dulu.
            Jika masih sama, berarti data periode tersebut belum tersedia dari API atau belum ada transaksi.
          </div>
        </Offcanvas.Body>
      </Offcanvas>
    </LayoutDashboard>
  );
};

export default BookkeepingPage;
